/**
 * Hooks React Query para o módulo de Projetos (Helpdesk)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { projectsService } from '../services';
import type {
  CreateProjectRequest,
  CreateProjectTaskRequest,
  LinkTicketRequest,
  ProjectDropdownItem,
  ProjectGanttResponse,
  ProjectListResponse,
  ProjectQuery,
  ProjectResponse,
  TaskDependencyStatusResponse,
  UpdateProjectRequest,
  UpdateProjectTaskRequest,
} from '../types';

/**
 * Hook para buscar a listagem paginada de projetos (com métricas agregadas).
 */
export function useProjects(query?: ProjectQuery, options?: { enabled?: boolean }) {
  return useQuery<ProjectListResponse>({
    queryKey: ['projects', query],
    queryFn: () => projectsService.getProjects(query),
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook para buscar um projeto por id.
 */
export function useProjectById(id?: string) {
  return useQuery<ProjectResponse>({
    queryKey: ['project', id],
    queryFn: () => projectsService.getProjectById(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook para buscar o cronograma (Gantt) de um projeto.
 */
export function useProjectGantt(id?: string) {
  return useQuery<ProjectGanttResponse>({
    queryKey: ['project-gantt', id],
    queryFn: () => projectsService.getGantt(id!),
    enabled: !!id,
    staleTime: 15 * 1000,
  });
}

/**
 * Hook para criação composta de projeto (projeto + fases + tarefas).
 */
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProjectRequest) => projectsService.createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects-dropdown'] });
      toast.success('Projeto criado com sucesso');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Erro ao criar projeto');
    },
  });
}

/**
 * Hook para atualizar dados do projeto.
 */
export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectRequest }) =>
      projectsService.updateProject(id, data),
    onSuccess: (project, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['project-gantt', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['projects-dropdown'] });
      if (project?.id) {
        queryClient.setQueryData(['project', project.id], project);
      }
      toast.success('Projeto atualizado com sucesso');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Erro ao atualizar projeto');
    },
  });
}

/**
 * Hook para excluir o projeto.
 */
export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => projectsService.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects-dropdown'] });
      toast.success('Projeto excluído com sucesso');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Erro ao excluir projeto');
    },
  });
}

/**
 * Hook para criar uma nova tarefa (materializa ticket novo).
 */
export function useAddProjectTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: CreateProjectTaskRequest }) =>
      projectsService.addTask(projectId, data),
    onSuccess: (_task, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-gantt', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Tarefa criada com sucesso');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Erro ao criar tarefa');
    },
  });
}

/**
 * Hook para vincular um ticket já existente como tarefa do projeto.
 */
export function useLinkProjectTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: LinkTicketRequest }) =>
      projectsService.linkTicket(projectId, data),
    onSuccess: (_task, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-gantt', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Ticket vinculado ao projeto com sucesso');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Erro ao vincular ticket');
    },
  });
}

/**
 * Hook para atualizar uma tarefa (datas, fase, dependências, marco — ex: drag no Gantt).
 */
export function useUpdateProjectTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      taskId,
      data,
    }: {
      projectId: string;
      taskId: string;
      data: UpdateProjectTaskRequest;
    }) => projectsService.updateTask(projectId, taskId, data),
    onSuccess: (_task, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-gantt', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Erro ao atualizar tarefa');
    },
  });
}

/**
 * Hook para remover uma tarefa (soft delete + desvincula o ticket).
 */
export function useRemoveProjectTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, taskId }: { projectId: string; taskId: string }) =>
      projectsService.removeTask(projectId, taskId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-gantt', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Tarefa removida com sucesso');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Erro ao remover tarefa');
    },
  });
}

/**
 * Hook cross-module: lista leve de projetos para selectors/dropdowns
 * (`GET /projects/dropdown`, sem permissionMiddleware).
 */
export function useProjectsDropdown() {
  return useQuery<ProjectDropdownItem[]>({
    queryKey: ['projects-dropdown'],
    queryFn: () => projectsService.getForDropdown(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook cross-module: estado de dependências de um ticket (consumido pelo
 * kanban de tickets para bloquear/avisar sobre predecessoras pendentes).
 */
export function useProjectTaskStatus(ticketId?: string) {
  return useQuery<TaskDependencyStatusResponse>({
    queryKey: ['project-task-status', ticketId],
    queryFn: () => projectsService.getTaskStatusByTicket(ticketId!),
    enabled: !!ticketId,
    staleTime: 30 * 1000,
  });
}
